import { type ComponentProps, useCallback, useState } from "react";
import { DetailStackHost } from "#app/components/detail-view/detail-stack";
import { ContainEntityDetail } from "#app/components/entity";
import {
  Box,
  DialogHost,
  Drawer,
  DrawerFooter,
  DrawerHeader,
  DrawerPanel,
  DrawerPopup,
  DrawerTitle,
  DrawerTitleRow,
} from "@salestrak/ui";
import { IssueMenu } from "#app/components/issue-actions/issue-menu";
import { IssueBreadcrumb } from "./issue-view-header";
import { useActivityContextValue } from "#app/hooks/issue-view/use-activity-context-value";
import {
  CreateIssueProvider,
  useCreateIssueMode,
} from "#app/lib/create-issue-context";
import { ActivityProvider } from "./activity/activity-context";
import { NoteComposer } from "./activity/note-composer";
import {
  DockedComposerProvider,
  useDockedComposer,
} from "./activity/docked-composer-context";
import { IssueBody } from "./issue-body";
import { useIssueViewDialogState } from "./issue-view-dialog-state";

// Create mode: the DrawerPanel is the scroller. IssueBody owns its own
// horizontal padding, so the panel contributes only the top gap; the bottom
// safe area is DrawerFooter/DrawerPopup's job, not a measured inset. View
// mode instead uses `variant="workspace"` so the tab strip stays fixed under
// the title and each TabsPanel scrolls, exactly as the desktop dialog lays out.
const PANEL_INSET = "px-0 pt-3";

// The docked comment bar: a hairline over the feed, 8px of air round the
// field, and — while it holds focus — a bottom pad that reaches the top of
// the keyboard (never less than the home-indicator inset). The `in-[…]` pad
// repeats the bare footer's own variant string so it overrides it.
const COMMENT_BAR_FOOTER =
  "border-t px-2 pt-2 in-[[data-slot=drawer-popup]:has([data-slot=drawer-panel])]:pt-2 pb-[calc(env(safe-area-inset-bottom,0px)+--spacing(2))] focus-within:pb-[calc(max(env(safe-area-inset-bottom,0px),var(--drawer-keyboard-inset,0px))+--spacing(2))]";

export function IssueViewSheet() {
  const dialog = useIssueViewDialogState();
  const [createCanAutoFocus, setCreateCanAutoFocus] = useState(false);
  const [viewHasSettled, setViewHasSettled] = useState(false);

  const viewState = dialog.state?.mode === "view" ? dialog.state : null;
  const createState = dialog.state?.mode === "create" ? dialog.state : null;

  // Gate the note auto-scroll until the view sheet finishes its open animation.
  // scrollIntoView mid-snap fights the drawer's transform, landing the scroll
  // off-target. `onOpenChangeComplete` is the settle signal.
  const handleViewOpenComplete = useCallback(
    (open: boolean) => setViewHasSettled(open),
    [],
  );

  // Gate title autofocus the same way: focusing mid-animation opens the virtual
  // keyboard, which resizes the visual viewport and desyncs the drawer's
  // transform math.
  const handleCreateOpenComplete = useCallback(
    (open: boolean) => setCreateCanAutoFocus(open),
    [],
  );

  const onOpenChange = useCallback(
    (open: boolean) => {
      if (!open) dialog.closeIssue();
    },
    [dialog],
  );

  // Call unconditionally (rules of hooks). Inner query skips when issueId is empty.
  const activity = useActivityContextValue(
    viewState?.issue.issueId ?? "",
    viewState?.focusNoteId,
    viewHasSettled,
    viewState?.focusUnread,
  );
  const showActivityFooter = !!viewState && dialog.activeTab === "activity";

  const bodyProps = {
    activeTab: dialog.activeTab,
    setActiveTab: dialog.setActiveTab,
    contentMaxWidth: undefined,
    isMobile: true,
  };

  return (
    <DetailStackHost>
      {viewState && (
        <Drawer
          open
          onOpenChange={onOpenChange}
          onOpenChangeComplete={handleViewOpenComplete}
          // The project is worked in: the top stop alone.
          size="full"
        >
          <DrawerPopup showBar surface="background">
            <DrawerHeader>
              <Box direction="row" align="center" justify="between" gap="sm">
                {/* The desktop dialog's header trail, verbatim: the workflow
                    as a glyph, then the name on one truncating line (Spencer,
                    2026-09-29 — a bare repeated title and a scroll-collapsing
                    one were both tried here first). The body still carries
                    the big editable title; the trail is the sheet's
                    wayfinding, not a second title. The sheet's accessible
                    name is the project's. */}
                <Box direction="row" align="center" fill="flex" minWidth={0}>
                  <DrawerTitle visuallyHidden>
                    {viewState.issue.name ?? "Project"}
                  </DrawerTitle>
                  <IssueBreadcrumb issue={viewState.issue} />
                </Box>
                {/* The follow-up verb rides the tab strip's end (IssueViewHeader),
                    same as desktop — the header row carries only the menu. */}
                <Box direction="row" align="center" gap="xs" shrink={false}>
                  <IssueMenu
                    issue={viewState.issue}
                    readOnly={!viewState.issue.viewerCanEdit}
                  />
                </Box>
              </Box>
            </DrawerHeader>
            {/* Keyed by issue: a reply or edit aimed at one Project never
                carries over to the next. */}
            <DockedComposerProvider key={viewState.issue.issueId}>
              <DrawerPanel variant="workspace">
                <IssueBody
                  {...bodyProps}
                  issue={viewState.issue}
                  focusNoteId={viewState.focusNoteId}
                  focusUnread={viewState.focusUnread}
                  focusCustomFieldId={viewState.focusCustomFieldId}
                  focusTitleNonce={viewState.focusTitleNonce}
                  activityContext={activity}
                />
              </DrawerPanel>
              <CommentBarFooter
                show={showActivityFooter}
                issueId={viewState.issue.issueId}
                activityValue={activity.value}
              />
            </DockedComposerProvider>
            {/* App-level dialogs opened from this sheet stack on it. */}
            <DialogHost />
          </DrawerPopup>
        </Drawer>
      )}

      {createState && (
        <Drawer
          open
          onOpenChange={onOpenChange}
          onOpenChangeComplete={handleCreateOpenComplete}
          size="full"
        >
          <DrawerPopup showBar surface="background">
            <CreateIssueProvider
              key={
                createState.address?.wholeAddress ??
                (createState.address?.latLng
                  ? `${createState.address.latLng.lat},${createState.address.latLng.lng}`
                  : "no-address")
              }
              initialWorkflowId={createState.workflowId}
              initialStageId={createState.stageId}
              initialAddress={createState.address}
              initialName={createState.name}
              initialIssueSourceId={createState.issueSourceId}
              initialTeamId={createState.teamId}
              source={createState.source}
              onCreated={dialog.onCreated}
              canAutoFocus={createCanAutoFocus}
            >
              <ContainEntityDetail>
                <CreateSheetHeader closeIssue={dialog.closeIssue} />
                <DrawerPanel className={PANEL_INSET}>
                  <IssueBody {...bodyProps} issue={null} />
                </DrawerPanel>
              </ContainEntityDetail>
            </CreateIssueProvider>
            {/* App-level dialogs opened from this sheet stack on it. */}
            <DialogHost />
          </DrawerPopup>
        </Drawer>
      )}
    </DetailStackHost>
  );
}

/**
 * The comment bar rides the keyboard: while the field holds focus the
 * footer's bottom pad grows by the measured keyboard inset, so the bar sits
 * on the keys and the feed above shrinks to the strip that is left — never a
 * second bar under the browser's accessory row. No transition: the keyboard
 * provider measures once per viewport event, and a moving footer would
 * leave its reading stale. It stands on the Activity tab, and anywhere else
 * while a reply or edit started from a comment (the Summary's Comments
 * widget) is being written.
 */
function CommentBarFooter({
  show,
  issueId,
  activityValue,
}: {
  show: boolean;
  issueId: string;
  activityValue: ComponentProps<typeof ActivityProvider>["value"];
}) {
  const dock = useDockedComposer();
  if (!show && !dock?.target) return null;
  return (
    <DrawerFooter variant="bare" className={COMMENT_BAR_FOOTER}>
      <ActivityProvider value={activityValue}>
        <NoteComposer issueId={issueId} docked />
      </ActivityProvider>
    </DrawerFooter>
  );
}

/** The drawer header takes ordinary children, so the outline × and Create
 *  are real Buttons flanking the title. */
function CreateSheetHeader({ closeIssue }: { closeIssue: () => void }) {
  const { submit, isSubmitting } = useCreateIssueMode()!;
  return (
    <DrawerHeader>
      <DrawerTitleRow
        dismiss={{ onClick: closeIssue }}
        primary={{
          label: "Create",
          onClick: submit,
          disabled: isSubmitting,
          loading: isSubmitting,
        }}
      >
        <DrawerTitle>Create Project</DrawerTitle>
      </DrawerTitleRow>
    </DrawerHeader>
  );
}
